<script setup lang="ts">
  import { TypoVariant, Typo, Button, ButtonSize, Icon } from '@travelata-libs/ui-kit';
  import { type EnumValues, pluralize } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { RatingSize } from './Rating.types';

  interface RatingProps {
    size?: EnumValues<RatingSize>;
    short?: boolean;
    rating?: number;
    reviewsCount?: number;
    reviewsLabels?: string[];
    readonly?: boolean;
  }

  const props = withDefaults(defineProps<RatingProps>(), {
    size: RatingSize.S,
    short: true,
    rating: 0,
    reviewsCount: 0,
    reviewsLabels: () => ['отзыв', 'отзыва', 'отзывов'],
    readonly: false,
  });

  interface RatingEmits {
    (e: 'reviews-click'): void;
  }

  const emit = defineEmits<RatingEmits>();

  const ratingVariant = computed(() => {
    const typeVariants: Record<RatingSize, TypoVariant> = {
      [RatingSize.L]: TypoVariant.Heading3,
      [RatingSize.S]: TypoVariant.Heading6,
    };
    return typeVariants[props.size];
  });

  const reviewsCountButtonSize = computed(() => {
    const typeVariants: Record<RatingSize, ButtonSize> = {
      [RatingSize.L]: ButtonSize.M,
      [RatingSize.S]: ButtonSize.S,
    };
    return typeVariants[props.size];
  });

  const reviewsCountVariant = computed(() => {
    const typeVariants: Record<RatingSize, TypoVariant> = {
      [RatingSize.L]: TypoVariant.BodyLRegular,
      [RatingSize.S]: TypoVariant.BodyMRegular,
    };
    return typeVariants[props.size];
  });

  const ratingFill = computed(() => {
    if (props.rating >= 4) return 'best';

    if (props.rating >= 3 && props.rating < 4) return 'good';

    if (props.rating >= 1 && props.rating < 3) return 'fine';

    return 'bad';
  });

  const ratingText = computed(() => {
    return props.rating
      ? new Intl.NumberFormat('ru-RU', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(props.rating)
      : '-';
  });

  const reviewsCountText = computed(() => {
    const pluralizedreviewsCount = pluralize(props.reviewsLabels, props.reviewsCount, false);
    const formattedreviewsCount = new Intl.NumberFormat('ru-RU', {}).format(props.reviewsCount);
    return props.reviewsCount ? `${formattedreviewsCount} ${pluralizedreviewsCount}` : 'Нет отзывов';
  });

  const reviewsCountButtonText = computed(() => {
    if (!props.short) return 'Рейтинг';

    return reviewsCountText.value;
  });

  const buttonDisabled = computed(() => {
    return !props.reviewsCount && props.short;
  });

  const onClick = () => {
    emit('reviews-click');
  };
</script>

<template>
  <div
    :class="[
      'tl-rating',
      `tl-rating_size-${size}`,
      `tl-rating_fill-${ratingFill}`,
      {
        'tl-rating_empty-reviews': !reviewsCount,
      },
    ]"
    data-test-id="root"
  >
    <div class="tl-rating__wrapper">
      <Typo class="tl-rating__rating" :variant="ratingVariant" data-test-id="rating">
        {{ ratingText }}
      </Typo>
      <div class="tl-rating__reviews">
        <Icon v-if="short" size="m" name="chat" class="tl-rating__icon" />
        <Button
          :class="{ 'tl-rating__button-readonly': readonly }"
          type="dash-link"
          :size="reviewsCountButtonSize"
          :disabled="buttonDisabled"
          appearance="secondary"
          data-test-id="button-reviews"
          @click="onClick"
        >
          {{ reviewsCountButtonText }}
        </Button>
      </div>
    </div>
    <div v-if="!short" class="tl-rating__reviews-text">
      <div class="tl-rating__reviews-text-wrapper">
        <Icon size="m" name="chat" class="tl-rating__icon" />
        <Typo :variant="reviewsCountVariant" data-test-id="typo-reviews">
          {{ reviewsCountText }}
        </Typo>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-rating {
    $root: &;

    display: flex;
    gap: var(--spacing-xs);

    &__wrapper {
      display: flex;
      gap: var(--spacing-xs);
      padding-right: var(--spacing-xs);
    }

    &_fill-bad {
      #{$root}__rating {
        background-color: var(--color-text-tertiary);
      }
      #{$root}__wrapper {
        background-color: var(--color-slate-10);
      }
    }

    &__button-readonly {
      cursor: default;
    }

    &_fill-fine {
      #{$root}__rating {
        background-color: var(--color-orange-50);
      }
      #{$root}__wrapper {
        background-color: var(--color-orange-05);
      }
    }

    &_fill-good {
      #{$root}__rating {
        background-color: var(--color-lime-50);
      }
      #{$root}__wrapper {
        background-color: var(--color-lime-05);
      }
    }

    &_fill-best {
      #{$root}__rating {
        background-color: var(--color-green-50);
      }
      #{$root}__wrapper {
        background-color: var(--color-green-05);
      }
    }

    &_size-s {
      height: 36px;
      #{$root}__rating {
        width: 40px;
      }
    }

    &_size-l {
      height: 48px;
      #{$root}__rating {
        width: 56px;
      }
    }

    &__rating {
      align-items: center;
      color: var(--color-text-inverse-white);
      display: flex;
      justify-content: center;
    }

    &__reviews,
    &__reviews-text {
      align-items: center;
      color: var(--color-blue-60);
      display: flex;
      gap: var(--spacing-xxs);
      justify-content: center;
    }

    &__reviews-text-wrapper {
      align-items: flex-end;
      display: flex;
      gap: var(--spacing-xxs);
    }

    &__wrapper,
    &__rating {
      border-radius: var(--rounding-s);
    }

    &__icon {
      fill: var(--color-text-secondary);
    }

    &_empty-reviews {
      #{$root}__icon {
        fill: var(--color-text-tertiary);
      }
      #{$root}__reviews-text {
        color: var(--color-text-tertiary);
      }
    }
  }
</style>
